import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Building2, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserPanel = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLimit, setSelectedLimit] = useState(null);

  const limitOptions = [
    { value: 10, label: '10 utenti' },
    { value: 50, label: '50 utenti' },
    { value: 100, label: '100 utenti' },
    { value: 250, label: '250 utenti (100+)' },
  ];

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/system/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/system/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!statsRes.ok || !settingsRes.ok) {
        throw new Error('Errore nel caricamento dei dati');
      }

      const statsData = await statsRes.json();
      const settingsData = await settingsRes.json();

      setStats(statsData);
      setSettings(settingsData);
      setSelectedLimit(settingsData.max_users_limit);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Redirect if not super_admin
    if (user && user.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  const handleSaveLimit = async () => {
    if (selectedLimit === settings?.max_users_limit) {
      toast.info('Nessuna modifica da salvare');
      return;
    }

    // Check if new limit is lower than current users
    if (selectedLimit < stats?.total_users) {
      toast.error(`Impossibile impostare un limite (${selectedLimit}) inferiore agli utenti attuali (${stats.total_users})`);
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/system/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ max_users_limit: selectedLimit })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore nel salvataggio');
      }

      toast.success('Limite utenti aggiornato con successo');
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.message || 'Errore nel salvataggio delle impostazioni');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="user-panel-loading">
        <Loader2 className="w-8 h-8 text-[#3BA99C] animate-spin" />
      </div>
    );
  }

  const usagePercentage = stats ? Math.round((stats.total_users / stats.max_users_limit) * 100) : 0;
  const isNearLimit = usagePercentage >= 80;
  const isAtLimit = usagePercentage >= 100;

  return (
    <div className="space-y-6" data-testid="user-panel">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-[#3BA99C]/10 rounded-lg">
          <Shield className="w-6 h-6 text-[#3BA99C]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pannello Utenti</h1>
          <p className="text-sm text-slate-500">Gestione limiti e statistiche sistema</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Users Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="total-users-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-slate-600">Totale Utenti</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-slate-900">{stats?.total_users || 0}</span>
            <span className="text-sm text-slate-500">/ {stats?.max_users_limit || 0}</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">Esclusi i Super Admin</p>
        </div>

        {/* Total CAA Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="total-caa-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <Building2 className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="text-sm font-medium text-slate-600">Totale CAA</span>
          </div>
          <span className="text-3xl font-bold text-slate-900">{stats?.total_caa || 0}</span>
        </div>

        {/* Users Remaining Card */}
        <div className={`bg-white rounded-xl border p-5 shadow-sm ${
          isAtLimit ? 'border-red-300 bg-red-50' : isNearLimit ? 'border-amber-300 bg-amber-50' : 'border-slate-200'
        }`} data-testid="users-remaining-card">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${
              isAtLimit ? 'bg-red-100' : isNearLimit ? 'bg-amber-100' : 'bg-slate-100'
            }`}>
              {isAtLimit ? (
                <AlertTriangle className="w-5 h-5 text-red-600" />
              ) : (
                <Users className={`w-5 h-5 ${isNearLimit ? 'text-amber-600' : 'text-slate-600'}`} />
              )}
            </div>
            <span className="text-sm font-medium text-slate-600">Utenti Rimanenti</span>
          </div>
          <span className={`text-3xl font-bold ${
            isAtLimit ? 'text-red-600' : isNearLimit ? 'text-amber-600' : 'text-slate-900'
          }`}>
            {stats?.users_remaining || 0}
          </span>
          {isAtLimit && (
            <p className="text-xs text-red-600 mt-1 font-medium">Limite raggiunto!</p>
          )}
          {isNearLimit && !isAtLimit && (
            <p className="text-xs text-amber-600 mt-1">Limite quasi raggiunto</p>
          )}
        </div>
      </div>

      {/* Usage Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-slate-700">Utilizzo Utenti</span>
          <span className={`text-sm font-semibold ${
            isAtLimit ? 'text-red-600' : isNearLimit ? 'text-amber-600' : 'text-[#3BA99C]'
          }`}>
            {usagePercentage}%
          </span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-[#3BA99C]'
            }`}
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          />
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {stats?.total_users || 0} utenti su {stats?.max_users_limit || 0} disponibili
        </p>
      </div>

      {/* Settings Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Impostazioni Limite Utenti</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Numero massimo di utenti creabili
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {limitOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedLimit(option.value)}
                  disabled={option.value < stats?.total_users}
                  className={`p-3 rounded-lg border-2 transition-all text-center ${
                    selectedLimit === option.value
                      ? 'border-[#3BA99C] bg-[#3BA99C]/5 text-[#3BA99C]'
                      : option.value < stats?.total_users
                      ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                      : 'border-slate-200 hover:border-slate-300 text-slate-700'
                  }`}
                  data-testid={`limit-option-${option.value}`}
                >
                  <span className="font-semibold">{option.label}</span>
                  {option.value < stats?.total_users && (
                    <p className="text-xs text-slate-400 mt-1">Non disponibile</p>
                  )}
                </button>
              ))}
            </div>
            {selectedLimit !== settings?.max_users_limit && (
              <p className="text-sm text-amber-600 mt-2">
                ⚠️ Hai modificato il limite. Ricorda di salvare.
              </p>
            )}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              Limite attuale: <strong>{settings?.max_users_limit || 250}</strong> utenti
            </p>
            <button
              onClick={handleSaveLimit}
              disabled={saving || selectedLimit === settings?.max_users_limit}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                saving || selectedLimit === settings?.max_users_limit
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-[#3BA99C] text-white hover:bg-[#2D8A7F]'
              }`}
              data-testid="save-limit-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Salva Limite
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Come funziona il limite utenti?</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Il limite si applica a tutti gli utenti (admin e utenti), esclusi i Super Admin</li>
              <li>Se un admin tenta di creare utenti oltre il limite, riceverai una email di alert</li>
              <li>Il limite si applica sia alla creazione singola che all'import Excel</li>
              <li>Puoi modificare il limite in qualsiasi momento (non può essere inferiore agli utenti attuali)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPanel;
